import { Link, useLocation } from "wouter";
import { Menu, LogOut, User, Wallet } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { ThemeToggle } from "./ThemeToggle";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { NotificationBell } from "./NotificationBell";
import { useIsAuthenticated, useLogout, getCurrentUser } from "@/hooks/use-auth";
import { useWalletBalance } from "@/hooks/use-wallet";

export function Navigation() {
  const [location, setLocation] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const isAuthenticated = useIsAuthenticated();
  const logout = useLogout();
  const user = getCurrentUser();
  const { data: walletData } = useWalletBalance();
  const walletBalance = walletData?.balance ?? 0;

  const handleLogout = () => {
    logout();
    setLocation("/");
  };

  const navItems = isAuthenticated
    ? [
        { href: "/", label: t("nav.home") },        // الرئيسية
        { href: "/profile", label: t("nav.profile") }, // ملفي
        { href: "/services", label: t("nav.services") }, // الخدمات
        { href: "/booking", label: t("nav.booking") }, // احجز الآن
        { href: "/orders", label: t("nav.orders") },   // طلباتي
        { href: "/gifts", label: t("nav.gifts") },     // هدايا
        { href: "/redeem", label: t("nav.redeem") },   // استبدال هدية
      ]
    : [
        { href: "/", label: t("nav.home") },
        { href: "/services", label: t("nav.services") },
        { href: "/booking", label: t("nav.booking") },
        { href: "/about", label: t("nav.about") },
        { href: "/redeem", label: t("nav.redeem") },
      ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-md">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between gap-4">
        <Link href="/" className="flex items-center group" aria-label={t("brand.name")}>
          {/* Navy long logo on light backgrounds, white on dark */}
          <img
            src="/brand/crystal-wash-long-navy.png"
            alt={t("brand.name")}
            className="h-9 sm:h-10 w-auto block dark:hidden"
          />
          <img
            src="/brand/crystal-wash-long-white.png"
            alt={t("brand.name")}
            className="h-9 sm:h-10 w-auto hidden dark:block"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navItems.map((item) => (
            <Link 
              key={item.href} 
              href={item.href}
              className={`text-sm font-medium transition-colors hover:text-primary ${
                location === item.href ? "text-primary font-semibold" : "text-muted-foreground"
              }`}
            >
              {item.label}
            </Link>
          ))}
          <LanguageSwitcher />
          <ThemeToggle />
          {isAuthenticated && <NotificationBell />}
          {isAuthenticated ? (
            <>
              <div className="flex flex-col items-end text-end">
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <User className="h-3.5 w-3.5" />
                  {user?.name || user?.phone}
                </span>
                <span className="text-xs font-medium flex items-center gap-1 text-primary">
                  <Wallet className="h-3 w-3" />
                  {t("nav.wallet")}:{" "}
                  {walletBalance.toFixed(3)} {t("services.currency")}
                </span>
              </div>
              <Button size="icon" variant="ghost" onClick={handleLogout} data-testid="button-logout">
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Link href="/booking">
              <Button size="sm" className="btn-hover" data-testid="button-get-started">
                {t("nav.getStarted")}
              </Button>
            </Link>
          )}
        </nav>

        {/* Mobile Nav */}
        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher />
          <ThemeToggle />
          {isAuthenticated && <NotificationBell />}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="border-0">
              <div className="flex flex-col gap-6 mt-8">
                {navItems.map((item) => (
                  <Link 
                    key={item.href} 
                    href={item.href}
                    className={`text-lg font-medium transition-colors hover:text-primary ${
                      location === item.href ? "text-primary" : "text-muted-foreground"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
                {isAuthenticated ? (
                  <>
                    <Button variant="outline" className="w-full" onClick={() => { handleLogout(); setIsOpen(false); }}>
                      <LogOut className="h-4 w-4 me-2" />
                      {t("login.logout")}
                    </Button>
                  </>
                ) : (
                  <Link href="/booking" onClick={() => setIsOpen(false)}>
                    <Button className="w-full">
                      {t("nav.booking")}
                    </Button>
                  </Link>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
